import { toast } from 'sonner';
import { useApiQuery } from '@/hooks/useApiQuery';
import { createMutationHook } from '@/hooks/lib/createMutationHook';
import { apiDelete, apiGet, apiPatch, apiPost, operationsClient } from '@/lib/api';
import { settingsEndpoints } from '@/lib/api/endpoints';
import { settingsKeys } from '@/lib/queryKeys';
import type {
  BookingLink,
  CreateBookingLinkDto,
  UpdateBookingLinkDto,
} from '@/types/bookingConfig';

export function useBookingLinks() {
  return useApiQuery<BookingLink[]>({
    queryKey: settingsKeys.bookingLinks(),
    queryFn: () => apiGet<BookingLink[]>(operationsClient, settingsEndpoints.bookingLinks()),
    showErrorToast: false,
  });
}

export const useCreateBookingLink = createMutationHook<BookingLink, CreateBookingLinkDto>({
  mutationFn: (dto) =>
    apiPost<BookingLink, CreateBookingLinkDto>(
      operationsClient,
      settingsEndpoints.bookingLinks(),
      dto,
    ),
  onSuccess: (_data, _variables, queryClient) => {
    void queryClient.invalidateQueries({
      queryKey: settingsKeys.bookingLinks(),
    });
    toast.success('Booking link created');
  },
});

export const useUpdateBookingLink = createMutationHook<
  BookingLink,
  { id: string; dto: UpdateBookingLinkDto }
>({
  mutationFn: ({ id, dto }) =>
    apiPatch<BookingLink, UpdateBookingLinkDto>(
      operationsClient,
      settingsEndpoints.bookingLink(id),
      dto,
    ),
  onSuccess: (_data, _variables, queryClient) => {
    void queryClient.invalidateQueries({
      queryKey: settingsKeys.bookingLinks(),
    });
    toast.success('Booking link updated');
  },
});

export const useDeleteBookingLink = createMutationHook<void, string>({
  mutationFn: (id) => apiDelete(operationsClient, settingsEndpoints.bookingLink(id)),
  onSuccess: (_data, _variables, queryClient) => {
    void queryClient.invalidateQueries({
      queryKey: settingsKeys.bookingLinks(),
    });
    toast.success('Booking link removed');
  },
});
